import React, { forwardRef, type ElementType, type ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { getModernScrollbarThin } from '../../../theme/theme';

export interface ScrollablePanelProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
  dataTestId?: string;
  direction?: 'vertical' | 'horizontal' | 'both';
  scrollbar?: 'thin' | 'none';
}

export const ScrollablePanel = forwardRef<HTMLDivElement, ScrollablePanelProps>(
  function ScrollablePanel(
    {
      children,
      sx,
      dataTestId,
      direction = 'vertical',
      scrollbar = 'thin',
    },
    ref
  ) {
    const theme = useTheme();

    const baseSx: SxProps<Theme> = {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
    };

    const overflowSx: SxProps<Theme> =
      direction === 'horizontal'
        ? { overflowX: 'auto', overflowY: 'hidden' }
        : direction === 'both'
        ? { overflow: 'auto' }
        : { overflowX: 'hidden', overflowY: 'auto' };

    const scrollbarSx: SxProps<Theme> =
      scrollbar === 'none'
        ? {
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }
        : getModernScrollbarThin(theme, 'divider');

    const mergedSx: SxProps<Theme> = [
      baseSx,
      overflowSx,
      scrollbarSx,
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ];

    return (
      <Box
        ref={ref}
        data-testid={dataTestId}
        sx={mergedSx}
      >
        {children}
      </Box>
    );
  }
);

export default ScrollablePanel;
